import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import type { MovedToBackstockItem } from "models/audit/AuditDetailModel";
import { bulkMarkManualSoldOut } from "services/scratcherService";

export function useMarkMovedPackSoldOutMutation(
  storeId: string | null | undefined,
  startDate: string | null | undefined,
  endDate?: string | null | undefined
) {
  const queryClient = useQueryClient();
  const resolvedEndDate = endDate ?? startDate;

  return useMutation({
    mutationFn: async (item: MovedToBackstockItem) => {
      if (!storeId) {
        throw new Error("Store ID is required");
      }
      if (!item.gameId || !item.packId) {
        throw new Error("Game ID and Pack ID are required");
      }

      const response = await bulkMarkManualSoldOut(storeId, [
        { game_id: String(item.gameId), pack_id: String(item.packId) },
      ]);

      if (!response.success) {
        throw new Error(response.error || "Failed to mark pack sold out");
      }

      const result = response.data?.results?.[0];
      if (!result || result.status !== "marked") {
        throw new Error(result?.message || response.error || "Failed to mark pack sold out");
      }

      return { item, result };
    },
    onSuccess: async ({ item }) => {
      toast.success("Pack marked sold out");

      await Promise.all([
        startDate && resolvedEndDate
          ? queryClient.invalidateQueries({
              queryKey: ["audit-dashboard-daily-expanded-report", storeId, startDate, resolvedEndDate],
            })
          : Promise.resolve(),
        queryClient.invalidateQueries({ queryKey: ["pack-lifecycle", storeId, String(item.gameId), String(item.packId)] }),
        queryClient.invalidateQueries({ queryKey: ["game-history", storeId, String(item.gameId)] }),
        queryClient.invalidateQueries({ queryKey: ["game-pack-report", storeId] }),
        queryClient.invalidateQueries({ queryKey: ["current-inventory-status", storeId] }),
        queryClient.invalidateQueries({ queryKey: ["lottery-packs", storeId] }),
        queryClient.invalidateQueries({ queryKey: ["audit-dashboard-manual-sold-out-eligibility", storeId] }),
      ]);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to mark pack sold out");
    },
  });
}
